import { Button, LoadingOverlay, Badge, Accordion, Switch } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useCallback, useEffect } from 'react';
import type { FormValueTypes } from '~/common/notifications/types';
import { getNotificationConfigValues } from '~/common/notifications/types';
import { api } from '~/trpc-extra/api';
import { NotificationSubForm } from './NotificationsSubForm';
import { notifications } from '@mantine/notifications';

export default function NotificationsFormEmail({
  projectId,
  testSuiteId,
}: {
  projectId: string;
  testSuiteId?: string;
}) {
  const trpcUtils = api.useUtils();

  // Email specific queries
  const { data: emailConfig, isLoading: isLoadingEmailConfig } = api.project.getEmailConfig.useQuery({ projectId });
  const { data: suiteOverride, isLoading: isLoadingSuiteOverride } = api.testSuite.getEmailConfig.useQuery({
    projectId,
    testSuiteId: testSuiteId || '',
  });
  const { data: notifOverride, isLoading: isLoadingNotifOverride } = api.team.getEmailNotificationsOverride.useQuery();
  const { mutateAsync: setEmailNotifOverride, isPending: isSavingOverride } =
    api.team.setEmailNotificationsOverride.useMutation({
      onSuccess: () => {
        trpcUtils.team.getEmailNotificationsOverride.invalidate().catch(() => undefined);
      },
    });

  const { mutateAsync: saveEmailConfig, isPending: isSavingEmailConfig } = api.project.upsertEmailConfig.useMutation({
    onSuccess: () => {
      trpcUtils.project.getEmailConfig.invalidate({ projectId }).catch(() => undefined);
    },
  });

  const activeEmailConfig = suiteOverride || emailConfig;

  const getInitialValues = useCallback(() => {
    return getNotificationConfigValues(
      activeEmailConfig ?
        { ...activeEmailConfig, dailySummary: activeEmailConfig.dailySummary ?? false }
      : activeEmailConfig,
      { disableOverride: notifOverride },
    );
  }, [activeEmailConfig, notifOverride]);

  const form = useForm<FormValueTypes>({
    initialValues: {
      channel: '',
      testRunStarts: false,
      testRunFinishes: false,
      weeklySummary: false,
      dailySummary: false,
      endType: 'NONE',
      disableOverride: false,
    },
  });

  // load initial values once data has been fetched
  useEffect(() => {
    form.setValues(getInitialValues());
  }, [isLoadingEmailConfig, isLoadingSuiteOverride, isLoadingNotifOverride, getInitialValues, form.setValues]);

  return (
    <>
      <LoadingOverlay
        visible={
          isLoadingEmailConfig ||
          isLoadingSuiteOverride ||
          isLoadingNotifOverride ||
          isSavingOverride ||
          isSavingEmailConfig
        }
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
      <form
        onSubmit={form.onSubmit(async (values) => {
          try {
            await Promise.all([
              ...(!testSuiteId ? [setEmailNotifOverride({ disableOverride: values.disableOverride })] : []),
              saveEmailConfig({
                testSuiteRunStart: values.testRunStarts,
                testSuiteRunEnd:
                  values.testRunFinishes ?
                    values.endType === 'NONE' ?
                      'ALL_RESULTS'
                    : values.endType
                  : 'NONE',
                weeklySummary: values.weeklySummary,
                dailySummary: values.dailySummary,
                projectId,
                testSuiteId: testSuiteId || undefined,
              }),
            ]);
            notifications.show({
              message: 'Email notification config saved',
            });
            modals.closeAll();
          } catch (e) {
            console.error('Error saving notifications:', e);
            notifications.show({
              color: 'red',
              message: 'Failed to save email notification config',
            });
          }
        })}
      >
        <div className="text-lg font-medium">Email notifications</div>
        <div className="mt-2 text-sm text-stone-500 dark:text-stone-400">
          All members in this organization will receive these emails
        </div>

        <NotificationSubForm
          form={form}
          showWeeklySummaryField={!testSuiteId}
        />

        {!testSuiteId && (
          <Accordion
            className="mt-2"
            styles={{
              control: {
                padding: 0,
                backgroundColor: 'transparent',
              },
              content: {
                padding: 0,
                paddingBottom: 16,
              },
            }}
            defaultValue="personal-preferences"
          >
            <Accordion.Item value="personal-preferences">
              <Accordion.Control>
                <div className="flex items-center gap-4">
                  <div className="text-lg font-medium">Personal preferences</div>
                  <Badge
                    color="blue"
                    variant="light"
                    size="sm"
                    radius="xl"
                  >
                    Override
                  </Badge>
                </div>
              </Accordion.Control>
              <Accordion.Panel>
                <div className="flex flex-row items-center gap-x-2 rounded-md border px-6 py-6 dark:border-gray-700">
                  <Switch
                    {...form.getInputProps('disableOverride', { type: 'checkbox' })}
                    size="sm"
                  />
                  <div className="ml-3 flex flex-col">
                    <div className="pr-2 text-sm font-bold text-stone-700 dark:text-stone-300">
                      Disable all personal notifications
                    </div>
                    <div className="whitespace-nowrap text-xs text-stone-500 dark:text-stone-400">
                      Stops notifications from being sent to your email, regardless of organization settings
                    </div>
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}

        <div className="mt-8 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => modals.closeAll()}
          >
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </>
  );
}
